/**
 * GA4 Measurement Protocol `client_id` extraction from the `_ga` cookie.
 *
 * Phase H parity: the desktop app generates its own UUID-shaped MP
 * `client_id` (see `feagi-desktop/src-tauri/src/ga_client_id.rs`) and
 * forwards it on `/register` + `/install-attribution/link-user`. The web
 * portal needs an equivalent value so server-side MP events fired by
 * Composer for web-originated conversions use the **same** `client_id`
 * the browser-side `gtag` calls used — otherwise GA4 would treat the
 * server hit as a separate user / session, inflating active-user counts
 * and breaking path / funnel reports.
 *
 * The `_ga` cookie set by the GA4 JavaScript library has the shape:
 *     `GA1.<version>.<client_id>`
 * where the historical client_id segment is two dot-delimited numbers:
 *     `<random_10_digit_int>.<unix_seconds_ts>`
 *  e.g.  `GA1.1.1234567890.1700000000`
 *
 * Only the `<random>.<ts>` portion is the canonical MP `client_id`.
 * We slice everything after the first two segments to stay resilient
 * against future `_ga` layout changes (Google has iterated on this
 * cookie shape multiple times already).
 *
 * Scope constraints:
 * - NOT HttpOnly: the cookie is set by the in-browser GA library, not by
 *   our server, and the signup client needs to read it. No secret
 *   material is stored — only an opaque analytics identifier.
 * - Lifetime is controlled by GA4 (`cookie_expires`, default 2 years);
 *   we never write this cookie ourselves.
 * - Presence of the cookie already implies the user has granted
 *   `analytics_storage`, because our `gtag('consent', 'default', ...)`
 *   bootstrap denies `analytics_storage` in consent-required regions
 *   until the banner explicitly grants it. Callers should still gate
 *   forwarding on the stored consent record for belt-and-braces safety.
 */

export const GA_COOKIE_NAME = "_ga";
export const GA_CLIENT_ID_QUERY_PARAM = "ga_client_id";

/**
 * Upper bound for an MP `client_id`. GA4 accepts up to 128 chars. Real
 * `_ga`-derived values are ~20 chars; we cap at 128 to stay aligned with
 * the Composer schema's `maxLength` and reject any cookie that has been
 * tampered with to be absurdly long.
 */
export const GA_CLIENT_ID_MAX_LENGTH = 128;

/**
 * Validate a candidate `client_id` string. The `_ga` cookie's payload
 * is `<digits>.<digits>` under the current GA4 library; we accept a
 * slightly broader URL-safe charset (digits, dots, dashes, underscores)
 * to stay robust across Google's iterations while still rejecting
 * clearly attacker-controlled garbage (whitespace, control chars,
 * non-ASCII, semicolons that would break the Set-Cookie format, etc).
 */
export function sanitizeGaClientId(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.length > GA_CLIENT_ID_MAX_LENGTH) return null;
  if (!/^[A-Za-z0-9._-]+$/.test(trimmed)) return null;
  return trimmed;
}

/**
 * Extract the MP `client_id` from a raw `_ga` cookie value.
 *
 * Input example: `"GA1.1.1234567890.1700000000"`
 * Output:        `"1234567890.1700000000"`
 *
 * Returns `null` when the input does not match the expected
 * `GA<digit>.<version>.<remainder>` layout (older / unrelated cookies,
 * or a mangled value).
 */
export function parseGaClientIdFromCookieValue(
  rawCookieValue: string | null | undefined
): string | null {
  if (!rawCookieValue) return null;
  const decoded = (() => {
    try {
      return decodeURIComponent(rawCookieValue);
    } catch {
      return rawCookieValue;
    }
  })();
  // Expected shape: "GA1.<version>.<client_id_segment_1>.<client_id_segment_2>...".
  // We split once on the first two dots and keep the remainder intact
  // so future multi-segment payloads continue to flow through.
  const match = decoded.match(/^GA\d+\.\d+\.(.+)$/);
  if (!match) return null;
  return sanitizeGaClientId(match[1]);
}

/**
 * Reads the GA4 MP `client_id` from a raw Cookie request header
 * (server-side usage — Next.js middleware / route handlers). Returns
 * `null` when the `_ga` cookie is absent or unparseable.
 */
export function readGaClientIdFromCookieHeader(
  header: string | null | undefined
): string | null {
  if (!header) return null;
  const match = header.match(new RegExp(`(?:^|; )${GA_COOKIE_NAME}=([^;]*)`));
  if (!match) return null;
  return parseGaClientIdFromCookieValue(match[1]);
}

/**
 * Client-only reader: extracts the MP `client_id` from `document.cookie`.
 * Returns `null` outside a browser context, when the cookie is absent
 * (e.g. analytics_storage denied so GA4 never wrote it), or when the
 * stored value fails sanitization.
 *
 * Unlike `getGoogleClickIdFromBrowser` there is no URL-param fallback:
 * the MP `client_id` exists purely as a GA4-managed cookie artifact;
 * it has no landing-URL analogue.
 */
export function getGaClientIdFromBrowser(): string | null {
  if (typeof document === "undefined") return null;
  return readGaClientIdFromCookieHeader(document.cookie);
}

/**
 * Resolve the browser's GA4 client ID without blocking authentication.
 *
 * The cookie is the fast path. When it has not been written yet, gtag's
 * asynchronous `get` command resolves the configured client ID after the GA
 * library is ready. The callback is never invoked for missing or invalid data.
 */
export function retrieveGaClientId(
  measurementId: string,
  onClientId: (_clientId: string) => void
): void {
  const cookieClientId = getGaClientIdFromBrowser();
  if (cookieClientId) {
    onClientId(cookieClientId);
    return;
  }
  if (
    typeof window === "undefined" ||
    typeof window.gtag !== "function" ||
    !measurementId
  ) {
    return;
  }

  window.gtag("get", measurementId, "client_id", (rawClientId: unknown) => {
    const clientId = sanitizeGaClientId(rawClientId);
    if (clientId) onClientId(clientId);
  });
}

interface ResolveGaClientIdOptions {
  measurementId?: string | null;
  maxAttempts?: number;
  intervalMs?: number;
}

/**
 * Resolve a GA client ID for web signup with a short bounded wait.
 *
 * First-time visitors can submit the signup form before GA has persisted the
 * `_ga` cookie. This helper polls for a brief window so `/register` can still
 * carry `ga_client_id` without blocking indefinitely.
 */
export async function resolveGaClientIdForSignup(
  options?: ResolveGaClientIdOptions
): Promise<string | null> {
  const measurementId = (options?.measurementId || "").trim();
  const maxAttempts = Math.max(1, options?.maxAttempts ?? 6);
  const intervalMs = Math.max(50, options?.intervalMs ?? 250);

  const immediate = getGaClientIdFromBrowser();
  if (immediate) return immediate;
  if (!measurementId) return null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const resolved = await new Promise<string | null>((resolve) => {
      let settled = false;
      const done = (value: string | null) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      retrieveGaClientId(measurementId, (clientId) => done(clientId));
      setTimeout(() => done(null), intervalMs);
    });
    if (resolved) return resolved;
  }
  return null;
}
